import React, { useEffect, useRef } from "react";
import { Pencil, PlusCircle, X } from "lucide-react";
import Blog from "./Blog";
import BlogAuthor from "./BlogAuthor";

interface EditBlogModalProps {
  isAdmin: boolean;
  onAuthorAdded: () => void;
  onSaveSuccess?: () => void;
}

const AddBlogAuthorModal: React.FC<EditBlogModalProps> = ({
  isAdmin,
  onAuthorAdded,
  onSaveSuccess,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal on escape or outside click
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isAdmin) {
    return null;
  }

  const handleSaveSuccess = () => {
    setIsOpen(false);
    if (onSaveSuccess) {
      onSaveSuccess();
      onAuthorAdded();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="p-2 rounded-md"
        title="Add Author"
      >
        <PlusCircle className="w-5 h-5 text-blue-500" />
      </button>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            ref={modalRef}
            className="bg-white rounded-lg w-[90vw] max-w-[1200px] max-h-[90vh] overflow-y-auto relative top-8"
          >
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold">Add Author</h2>
                <p className="text-gray-600">Add Author details</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              <BlogAuthor
                onSaveSuccess={handleSaveSuccess}
                onAuthorAdded={onAuthorAdded}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddBlogAuthorModal;
